import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Bot,
  CalendarClock,
  Calculator,
  FileText,
  LineChart,
  ListChecks,
  Repeat,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Lede, MarketingFooter, MarketingHeader, Section, SectionHeading } from "@/components/marketing/parts";
import { Button } from "@/components/ui/button";
import { trackCtaClick } from "@/lib/marketing-events";
import { supabase } from "@/integrations/supabase/client";

const TITLE = "How MyPriorities works — from customer to revenue to daily work";
const DESCRIPTION =
  "See how customers, contracts, pipeline, revenue and daily priorities connect in MyPriorities, plus the tools that support them.";

export const Route = createFileRoute("/how-it-works")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://mypriorities.co.uk/how-it-works" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://mypriorities.co.uk/how-it-works" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          description: DESCRIPTION,
          url: "https://mypriorities.co.uk/how-it-works",
          isPartOf: { "@type": "WebSite", url: "https://mypriorities.co.uk/" },
        }),
      },
    ],
  }),
  component: HowItWorks,
});

const STEPS = [
  {
    icon: Users,
    title: "1. Add the customer",
    body: "Create the account, its contacts and the relationship history so everyone works from the same record.",
  },
  {
    icon: Target,
    title: "2. Track the opportunity",
    body: "Open opportunities sit in a simple pipeline with stage, value and close date, so you always know what is live.",
  },
  {
    icon: FileText,
    title: "3. Record the agreement",
    body: "When a deal is won, the contract captures term dates, values and renewal points — no separate spreadsheet.",
  },
  {
    icon: LineChart,
    title: "4. See the revenue",
    body: "Contracted revenue, forecast and renewals are calculated from the same records, so reporting stays consistent.",
  },
  {
    icon: ListChecks,
    title: "5. Do the work",
    body: "Priorities and tasks link back to the customer, goal and pillar they serve, so effort follows the plan.",
  },
];

const CAPABILITIES = [
  {
    icon: ListChecks,
    title: "Priority matrix",
    body: "Sort work by urgency and importance, with due-date colour bands so this week never hides behind next month.",
  },
  {
    icon: Repeat,
    title: "Commitments and recurring work",
    body: "Regular meetings and recurring tasks live separately from your to-do list and reset automatically once completed.",
  },
  {
    icon: CalendarClock,
    title: "Capacity and time",
    body: "Weekly time estimates roll up to monthly and quarterly totals, splitting committed hours from flexible ones.",
  },
  {
    icon: Target,
    title: "Pillars and goals",
    body: "Professional and personal pillars sit above your goals, so each task and habit ladders up to something bigger.",
  },
  {
    icon: Sparkles,
    title: "Habits and wellbeing",
    body: "Track the personal habits behind good work, each tied to a personal goal and pillar.",
  },
  {
    icon: Calculator,
    title: "Business cases and charts",
    body: "Build quick commercial cases — revenue, cost, margin, ARR and TCV — and compare options in 2x2 charts.",
  },
  {
    icon: LineChart,
    title: "Performance reporting",
    body: "Review what was completed this week, month, quarter or year, and which goals and pillars it moved forward.",
  },
  {
    icon: Bot,
    title: "AI assistant",
    body: "Ask questions about your own priorities and commitments, and get suggestions on where to focus next.",
  },
];

function HowItWorks() {
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      setSignedIn(Boolean(session));
    });
    return () => data.subscription.unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <MarketingHeader signedIn={signedIn} />

      <Section eyebrow="How it works">
        <h1 className="mt-3 max-w-3xl text-4xl font-bold leading-tight sm:text-5xl">
          One record, followed all the way through
        </h1>
        <Lede>
          MyPriorities keeps customers, revenue and work joined up. Nothing is re-entered, and every
          number traces back to a record you can open.
        </Lede>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg">
            <Link to="/auth" onClick={() => trackCtaClick("start_for_free", "how_it_works_hero")}>
              Start for free <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="ghost">
            <Link to="/business" hash="plans">
              See pricing
            </Link>
          </Button>
        </div>
      </Section>

      <Section eyebrow="The journey" className="border-t border-border/60">
        <SectionHeading>From first conversation to finished work</SectionHeading>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map((step) => (
            <div key={step.title} className="panel p-6">
              <step.icon className="size-5 text-primary" />
              <h3 className="mt-4 font-display text-lg font-semibold">{step.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{step.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="What's inside" className="border-t border-border/60">
        <SectionHeading>The detail behind the three pillars</SectionHeading>
        <Lede>
          Each pillar is supported by focused tools. Use the ones you need and hide the rest — the
          hub is pick-and-order.
        </Lede>
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {CAPABILITIES.map((item) => (
            <div key={item.title} className="panel p-6">
              <item.icon className="size-5 text-primary" />
              <h3 className="mt-4 font-display text-lg font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{item.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="border-t border-border/60">
        <SectionHeading>Ready to see it with your own numbers?</SectionHeading>
        <Lede>Start with a customer and a contract — the rest builds from there.</Lede>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg">
            <Link to="/auth" onClick={() => trackCtaClick("start_for_free", "how_it_works_final_cta")}>
              Start for free <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="ghost">
            <Link to="/business" hash="enquiry">
              Talk to us
            </Link>
          </Button>
        </div>
      </Section>

      <MarketingFooter />
    </div>
  );
}
